import {
  Component,
  ElementRef,
  OnInit,
  viewChild,
  inject,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IConfigVecino, INuevoVecino, IUpdateConfigVecino } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { HelperService } from 'src/app/auxiliares/servicios/helper.service';
import { VecinosService } from 'src/app/entidades/vecinos/vecinos.service';
import { PaisConfigService } from 'src/app/auxiliares/servicios/pais-config.service';

@Component({
  selector: 'app-crear-editar-vecino',
  templateUrl: './crear-editar-vecino.component.html',
  styleUrls: ['./crear-editar-vecino.component.scss'],
  standalone: false,
})
export class CrearEditarVecinoComponent implements OnInit {
  data = inject<IConfigVecino>(MAT_DIALOG_DATA);
  private fb = inject(UntypedFormBuilder);
  private dialogRef =
    inject<MatDialogRef<CrearEditarVecinoComponent>>(MatDialogRef);
  private service = inject(VecinosService);
  private helper = inject(HelperService);
  private paisConfigService = inject(PaisConfigService);

  readonly inputDireccion = viewChild<ElementRef>('inputDireccion');
  public direcciones: string[] = [];
  public direccion = '';

  public formulario: UntypedFormGroup;
  public loading = true;
  public title = '';
  private codigoPais = '+54';

  constructor() {
    this.title = this.data?._id ? 'Editar Vecino' : 'Crear Vecino';
    // Inicialización básica del formulario
    this.formulario = this.fb.group({
      activo: [this.data?.activo || false],
      dni: [this.data?.datosPersonales?.dni || '', Validators.required],
      sexo: [this.data?.datosPersonales?.sexo, Validators.required],
      nombre: [this.data?.datosPersonales?.nombre || '', Validators.required],
      fechaNacimiento: [
        this.data?.datosPersonales?.fechaNacimiento || '',
        Validators.required,
      ],
      telefono: [
        this.data?.datosPersonales?.telefono || '',
        Validators.required,
      ],
      email: [this.data?.datosPersonales?.email || '', Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      // Obtener validaciones dinámicas según el país
      const [telefonoPattern, dniPattern, codigoPais] = await Promise.all([
        this.paisConfigService.getTelefonoPattern(),
        this.paisConfigService.getDniPattern(),
        this.paisConfigService.getCodigoPais(),
      ]);

      this.codigoPais = codigoPais;

      // Actualizar validaciones del formulario
      const telefonoControl = this.formulario.get('telefono');
      const dniControl = this.formulario.get('dni');

      if (telefonoPattern) {
        telefonoControl?.setValidators([
          Validators.required,
          Validators.pattern(telefonoPattern),
        ]);
        telefonoControl?.updateValueAndValidity();
      }

      if (dniPattern) {
        dniControl?.setValidators([
          Validators.required,
          Validators.pattern(dniPattern),
        ]);
        dniControl?.updateValueAndValidity();
      }
    } catch (error) {
      console.error('Error al cargar configuración de país:', error);
      this.helper.notifError('Error al cargar configuración de país');
    } finally {
      this.loading = false;
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  private getCreateData(): INuevoVecino {
    return {
      dni: this.formulario.get('dni')?.value,
      sexo: this.formulario.get('sexo')?.value,
      nombre: this.formulario.get('nombre')?.value,
      fechaNacimiento: this.formulario.get('fechaNacimiento')?.value,
      telefono: this.formulario.get('telefono')?.value,
      email: this.formulario.get('email')?.value,
      pais: this.codigoPais,
    };
  }

  private getUpdateData(): IUpdateConfigVecino {
    const data: IUpdateConfigVecino = {
      activo: this.formulario.get('activo')?.value,
      datosPersonales: {
        dni: this.formulario.get('dni')?.value,
        nombre: this.formulario.get('nombre')?.value,
        sexo: this.formulario.get('sexo')?.value,
        fechaNacimiento: this.formulario.get('fechaNacimiento')?.value,
        telefono: this.formulario.get('telefono')?.value,
        email: this.formulario.get('email')?.value,
      },
    };
    return data;
  }

  public async enviar(): Promise<void> {
    // this.loading = true;
    try {
      if (this.data?._id) {
        const data = this.getUpdateData();
        await firstValueFrom(this.service.editarConfig(this.data._id, data));
        this.helper.notifSuccess('Editado correctamente');
      } else {
        const data = this.getCreateData();
        await firstValueFrom(this.service.crear(data));
        this.helper.notifSuccess('Creado correctamente');
      }
      this.dialogRef.close(true);
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }
}
